import React, { useEffect, useMemo, useState, useCallback } from "react";
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  ResponsiveContainer,
} from "recharts"; // adjust path to your actual hook
import useApiFromGetPost from "../utils/useApiGetPost"

// Vibrant palette — enough distinct hues for 10 categories
const PASTEL_COLORS = [
  "#3DB2FF", // vivid blue
  "#FF5C7C", // vivid pink/red
  "#2ECC71", // vivid green
  "#FFA62B", // vivid orange
  "#9B5DE5", // vivid purple
  "#FF6FB5", // vivid pink
  "#12CBC4", // vivid teal
  "#FFD23F", // vivid yellow
  "#6C5CE7", // vivid indigo
  "#FF7849", // vivid coral
];

const DEFAULT_DATA = [];

// ---- date helpers ----
const MONTHS = [
  "jan", "feb", "mar", "apr", "may", "jun",
  "jul", "aug", "sep", "oct", "nov", "dec",
];

// Formats a Date as "10-jul-2026"
function formatApiDate(date) {
  const day = String(date.getDate()).padStart(2, "0");
  const month = MONTHS[date.getMonth()];
  const year = date.getFullYear();
  return `${day}-${month}-${year}`;
}

function addDays(date, delta) {
  const next = new Date(date);
  next.setDate(next.getDate() + delta);
  return next;
}

function isSameDay(a, b) {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

function chipLabel(date) {
  const today = new Date();
  const yesterday = addDays(today, -1);
  const tomorrow = addDays(today, 1);
  if (isSameDay(date, today)) return "Today";
  if (isSameDay(date, yesterday)) return "Yesterday";
  if (isSameDay(date, tomorrow)) return "Tomorrow";
  return formatApiDate(date);
}

// ---- date navigator chip ----
function DateNavChip({ date, onPrev, onNext }) {
  return (
    <div
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 4,
        background: "#f2f2f5",
        borderRadius: 999,
        padding: "4px 6px",
        userSelect: "none",
      }}
    >
      <button
        onClick={onPrev}
        aria-label="Previous day"
        style={{
          width: 26,
          height: 26,
          borderRadius: "50%",
          border: "none",
          background: "#fff",
          color: "#555",
          fontSize: 14,
          lineHeight: 1,
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          boxShadow: "0 1px 2px rgba(0,0,0,0.08)",
        }}
      >
        ‹
      </button>
      <span
        style={{
          fontSize: 13,
          fontWeight: 600,
          color: "#333",
          padding: "0 8px",
          minWidth: 90,
          textAlign: "center",
        }}
      >
        {chipLabel(date)}
      </span>
      <button
        onClick={onNext}
        aria-label="Next day"
        style={{
          width: 26,
          height: 26,
          borderRadius: "50%",
          border: "none",
          background: "#fff",
          color: "#555",
          fontSize: 14,
          lineHeight: 1,
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          boxShadow: "0 1px 2px rgba(0,0,0,0.08)",
        }}
      >
        ›
      </button>
    </div>
  );
}

function CustomTooltip({ active, payload, total }) {
  if (!active || !payload || !payload.length) return null;
  const { leave_key, count } = payload[0].payload;
  const pct = ((count / total) * 100).toFixed(1);
  return (
    <div
      style={{
        background: "#ffffff",
        border: "1px solid #e5e5e5",
        borderRadius: 8,
        padding: "8px 12px",
        boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
        fontSize: 13,
      }}
    >
      <div style={{ fontWeight: 600, color: "#333", marginBottom: 2 }}>
        {leave_key}
      </div>
      <div style={{ color: "#666" }}>
        {count} leaves ({pct}%)
      </div>
    </div>
  );
}

export default function AttendanceAnalysis({baseUrl, company_code_from_local_storage}) {
  const [activeIndex, setActiveIndex] = useState(null);
  const [selectedDate, setSelectedDate] = useState(new Date());

  const {
    loading: loadingApiDataDailySalesKPI,
    data: apiDataDailySalesKPI,
    get: getApiDataDailySalesKPI,
  } = useApiFromGetPost();

  const buildUrl = useCallback((date) => {
   
    const formattedDate = formatApiDate(date);
    return `${baseUrl}/api/HRDashboard/${company_code_from_local_storage}/-/LEAVECOUNT/${formattedDate}/${formattedDate}/-/-/-/-/-`;
  }, []);

  // Fetch on initial load and whenever the selected date changes
  useEffect(() => {
    const url = buildUrl(selectedDate);
    getApiDataDailySalesKPI(url);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate]);

  const handlePrev = () => setSelectedDate((d) => addDays(d, -1));
  const handleNext = () => setSelectedDate((d) => addDays(d, 1));

  // Normalize API response into [{ leave_key, count }] shape, falling back to defaults
  const data = useMemo(() => {
    if (!apiDataDailySalesKPI) return DEFAULT_DATA;
    if (Array.isArray(apiDataDailySalesKPI)) return apiDataDailySalesKPI;
    if (Array.isArray(apiDataDailySalesKPI.data)) return apiDataDailySalesKPI.data;
    return DEFAULT_DATA;
  }, [apiDataDailySalesKPI]);

  const total = useMemo(
    () => data.reduce((sum, d) => sum + d.count, 0),
    [data]
  );

  return (
    <div
      style={{
        width: "100%",
        maxWidth: 720,
        margin: "0 auto",
        fontFamily:
          "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
        background: "#fff",
        borderRadius: 16,
        padding: "1.5rem",
        boxSizing: "border-box",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 12,
          marginBottom: "1rem",
        }}
      >
        <div>
          <h2
            style={{
              margin: 0,
              fontSize: 18,
              fontWeight: 600,
              color: "#2a2a2a",
            }}
          >
            Leave breakdown
          </h2>
          <p style={{ margin: "4px 0 0", fontSize: 13, color: "#888" }}>
            {loadingApiDataDailySalesKPI
              ? "Loading..."
              : `Total: ${total}  -  ${data.length} categories`}
          </p>
        </div>

        <DateNavChip date={selectedDate} onPrev={handlePrev} onNext={handleNext} />
      </div>

      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          gap: "1.5rem",
        }}
      >
        {/* Chart */}
        <div
          style={{
            position: "relative",
            flex: "1 1 280px",
            minWidth: 240,
            height: 300,
          }}
        >
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={data}
                dataKey="count"
                nameKey="leave_key"
                cx="50%"
                cy="50%"
                innerRadius="60%"
                outerRadius="90%"
                paddingAngle={1}
                cornerRadius={4}
                onMouseEnter={(_, idx) => setActiveIndex(idx)}
                onMouseLeave={() => setActiveIndex(null)}
              >
                {data.map((entry, index) => (
                  <Cell
                    key={entry.leave_key}
                    fill={PASTEL_COLORS[index % PASTEL_COLORS.length]}
                    stroke="#fff"
                    strokeWidth={1}
                    opacity={
                      activeIndex === null || activeIndex === index ? 1 : 0.4
                    }
                    style={{ transition: "opacity 0.2s ease" }}
                  />
                ))}
              </Pie>
              <Tooltip content={<CustomTooltip total={total} />} />
            </PieChart>
          </ResponsiveContainer>

          {/* Center label */}
          <div
            style={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              textAlign: "center",
              pointerEvents: "none",
            }}
          >
            <div style={{ fontSize: 28, fontWeight: 700, color: "#333" }}>
              {total}
            </div>
            <div style={{ fontSize: 12, color: "#999" }}>Total </div>
          </div>
        </div>

        {/* Legend */}
        <div
          style={{
            flex: "1 1 260px",
            minWidth: 220,
            display: "flex",
            flexDirection: "column",
            gap: 8,
          }}
        >
          {data
            .slice()
            .sort((a, b) => b.count - a.count)
            .map((entry) => {
              const originalIndex = data.findIndex(
                (d) => d.leave_key === entry.leave_key
              );
              const pct = ((entry.count / total) * 100).toFixed(1);
              return (
                <div
                  key={entry.leave_key}
                  onMouseEnter={() => setActiveIndex(originalIndex)}
                  onMouseLeave={() => setActiveIndex(null)}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    padding: "6px 8px",
                    borderRadius: 8,
                    background:
                      activeIndex === originalIndex ? "#f7f7f7" : "transparent",
                    cursor: "default",
                    transition: "background 0.15s ease",
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <span
                      style={{
                        width: 10,
                        height: 10,
                        borderRadius: "50%",
                        background:
                          PASTEL_COLORS[originalIndex % PASTEL_COLORS.length],
                        flexShrink: 0,
                      }}
                    />
                    <span style={{ fontSize: 13, color: "#444" }}>
                      {entry.leave_key}
                    </span>
                  </div>
                  <span style={{ fontSize: 13, color: "#888", fontWeight: 500 }}>
                    {entry.count} ({pct}%)
                  </span>
                </div>
              );
            })}
        </div>
      </div>
    </div>
  );
}